// src/components/admin/GestionAusencias.jsx
console.log(
  "%cGestionAusencias.jsx DEBUG v12",
  "background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:4px"
);

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { ausenciasService } from "../../services/ausencias";
import BarraFiltros from "../shared/BarraFiltros";
import Paginacion from "../shared/Paginacion";
import ConfirmModal from "../shared/ConfirmModal";

/* =================== Utils =================== */
const STATUS_TO_ESTADO = {
  PENDING: "PENDIENTE",
  APPROVED: "APROBADA",
  REJECTED: "RECHAZADA",
  PENDIENTE: "PENDIENTE",
  APROBADA: "APROBADA",
  RECHAZADA: "RECHAZADA",
};
function aEstado(x) {
  const raw =
    x?.estado ??
    x?.request_status ??
    x?.status ??
    x?._estado ??
    x?._status ??
    null;
  if (!raw) return "PENDIENTE";
  const key = String(raw).toUpperCase();
  return STATUS_TO_ESTADO[key] ?? "PENDIENTE";
}

const fmtISODateSafely = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString("es-ES");
};
const fmtFechaHora = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleString("es-ES");
};

const strip = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const RANGOS = ["24h", "5d", "7d", "14d", "todos", "personalizado"];

function getFechaInicio(row) {
  return (
    row?.fecha_inicio ??
    row?.payload?.fecha_inicio ??
    row?.payload?.desde ??
    row?.ausencia?.fecha_inicio ??
    null
  );
}
function getFechaFin(row) {
  return (
    row?.fecha_fin ??
    row?.payload?.fecha_fin ??
    row?.payload?.hasta ??
    row?.ausencia?.fecha_fin ??
    null
  );
}

/* ---- Tipo / Subtipo ---- */
const toTitle = (s) => {
  if (!s) return "";
  const nice = String(s).replace(/_/g, " ").toLowerCase();
  return nice.replace(/\b\w/g, (m) => m.toUpperCase());
};

const LABELS = {
  VACACIONES: "Vacaciones",
  PERMISO: "Permisos",
  PERMISOS: "Permisos",
  CITA_MEDICA: "Cita médica",
  ACOMPANAMIENTO_MEDICO: "Acompañamiento médico",
  ACCIDENTE_ENFERMEDAD_FAMILIAR: "Accidente / enfermedad familiar",
  ASUNTOS_PROPIOS: "Asuntos propios",
  VIAJE: "Viaje",
  BAJA: "Baja",
  BAJA_IT: "Baja IT",
  CUIDADO_DEL_LACTANTE: "Cuidado del lactante",
  DEBER_INEXCUSABLE_PUBLICO_PERSONAL: "Deber inexcusable público/personal",
  EXAMEN_OFICIAL: "Examen oficial",
  OTRO: "Otro",
};
const labelize = (s) => {
  if (!s) return "";
  const key = String(s).trim().toUpperCase();
  return LABELS[key] || toTitle(s);
};

const normId = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[\/\-.]/g, " ")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();

const prettyTipo = (tipo, subtipo) => {
  const t = labelize(tipo || "—");
  const same = !!subtipo && normId(subtipo) === normId(tipo);
  const s = !subtipo || same ? "" : labelize(subtipo);
  return s ? `${t} · ${s}` : t;
};

const getTipoParaMostrar = (row) => {
  if (row?._is_request) {
    const baseTipo =
      row?.tipo_base ||
      row?.ausencia?.tipo ||
      row?.tipo_base_original ||
      row?.tipo_original ||
      row?.tipo;
    const baseSubtipo = row?.subtipo ?? row?.ausencia?.subtipo ?? null;
    return { tipo: baseTipo, subtipo: baseSubtipo };
  }
  return { tipo: row?.tipo, subtipo: row?.subtipo };
};

/* ---- Fechas / horas helpers ---- */
const fmtHora = (h) => (h ? String(h).slice(0, 5) : null);
const rangoFecha = (fi, ff) => {
  const A = fmtISODateSafely(fi);
  const B = fmtISODateSafely(ff);

  // si ambas existen y son el MISMO día, solo una fecha
  const same =
    fi && ff && String(fi).trim() === String(ff).trim() && A !== "—" && B !== "—";

  if (same) return A;
  if (A !== "—" && B !== "—") return `${A} → ${B}`;
  if (A !== "—") return A;
  if (B !== "—") return B;
  return "— —";
};
const rangoHoras = (hi, hf) => {
  const A = fmtHora(hi);
  const B = fmtHora(hf);
  if (!A && !B) return null;
  return `(${A || "—"}–${B || "—"})`;
};
const sameStr = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

/* ==== Parser defensivo del payload de CAMBIO (ampliado con nueva_*) ==== */
const parsePayload = (p) => {
  if (!p) return {};
  if (typeof p === "string") {
    try {
      const j = JSON.parse(p);
      return j || {};
    } catch {
      return {};
    }
  }
  return p || {};
};
const pickFirst = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "") ?? null;

const extractPropuesta = (row) => {
  // payload habitual
  const p0 = parsePayload(row?.payload);
  const cand = {
    ...p0,
    ...(p0?.payload || {}),
    ...(p0?.nuevo || {}),
    ...(p0?.new || {}),
    ...(p0?.change || {}),
    ...(p0?.cambio || {}),
  };

  // además soportamos si el backend ya expande nueva_*
  const fi = pickFirst(
    cand.fecha_inicio,
    cand.desde,
    cand.fechaDesde,
    cand.fecha_desde,
    cand.new_fecha_inicio,
    cand.nueva_fecha_inicio,
    cand.fecha_inicio_nueva,
    row?.nueva_fecha_inicio
  );
  const ff = pickFirst(
    cand.fecha_fin,
    cand.hasta,
    cand.fechaHasta,
    cand.fecha_hasta,
    cand.new_fecha_fin,
    cand.nueva_fecha_fin,
    cand.fecha_fin_nueva,
    row?.nueva_fecha_fin
  );
  const hi = pickFirst(
    cand.hora_inicio,
    cand.horaDesde,
    cand.hora_desde,
    cand.new_hora_inicio,
    cand.nueva_hora_inicio,
    row?.nueva_hora_inicio
  );
  const hf = pickFirst(
    cand.hora_fin,
    cand.horaHasta,
    cand.hora_hasta,
    cand.new_hora_fin,
    cand.nueva_hora_fin,
    row?.nueva_hora_fin
  );
  const parcial = pickFirst(
    cand.parcial,
    cand.is_parcial,
    cand.parcial_nueva,
    row?.nueva_parcial
  );

  return { fi, ff, hi, hf, parcial };
};

/* =================== Componente =================== */
export default function GestionAusencias({ token }) {
  // Datos
  const [ausenciasBase, setAusenciasBase] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("TODAS");
  const [busqueda, setBusqueda] = useState("");
  const [rangoSeleccionado, setRangoSeleccionado] = useState("todos");
  const [fechasPersonalizadas, setFechasPersonalizadas] = useState({
    inicio: "",
    fin: "",
  });

  // Paginación
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(8);

  // Acciones
  const [aprobarOpen, setAprobarOpen] = useState(false);
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  /* ----------- Carga ----------- */
  const cargarTodo = useCallback(async () => {
    setLoading(true);
    try {
      const base = await ausenciasService.listarTodas(token);
      const baseList = Array.isArray(base) ? base : [];
      const byId = new Map(baseList.map((a) => [a.id, a]));

      let cambios = [];
      let canceles = [];
      try {
        cambios = await ausenciasService.listarCambios(token);
      } catch {}
      try {
        canceles = await ausenciasService.listarCancelaciones(token);
      } catch {}

      const hydrate = (r) => {
        const base =
          byId.get(r._base_ausencia_id || r.ausencia_id) ||
          r.ausencia ||
          {};
        return {
          // base → solicitud
          ...base,
          ...r,

          // conservar la ausencia original para comparar en UI
          ausencia: r.ausencia || base,

          usuario_email:
            r.usuario_email ||
            base.usuario_email ||
            r.requested_by_email ||
            "—",

          // mantener campos de r si vienen, si no, de base (NO mezclamos payload aquí)
          fecha_inicio: r.fecha_inicio ?? base.fecha_inicio ?? null,
          fecha_fin: r.fecha_fin ?? base.fecha_fin ?? null,
          hora_inicio: r.hora_inicio ?? base.hora_inicio ?? null,
          hora_fin: r.hora_fin ?? base.hora_fin ?? null,

          parcial:
            (typeof r.parcial === "boolean" ? r.parcial : base.parcial) ??
            false,
          retribuida:
            (typeof r.retribuida === "boolean"
              ? r.retribuida
              : base.retribuida) ?? false,

          subtipo: r.subtipo ?? base.subtipo ?? null,
          tipo_base: r.tipo_base ?? base.tipo ?? null,

          estado: aEstado(r),

          _is_request: true,
          _request_kind:
            r._request_kind ||
            (r.tipo === "CANCELACION" ? "cancel" : "change"),
          tipo:
            r.tipo ||
            (r._request_kind === "cancel" ? "CANCELACION" : "CAMBIO"),
        };
      };

      setAusenciasBase(baseList);
      setSolicitudes([
        ...cambios.map((r) =>
          hydrate({ ...r, _request_kind: "change", tipo: "CAMBIO" })
        ),
        ...canceles.map((r) =>
          hydrate({ ...r, _request_kind: "cancel", tipo: "CANCELACION" })
        ),
      ]);
    } catch (e) {
      toast.error(e?.message || "Error al cargar ausencias");
      setAusenciasBase([]);
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    cargarTodo();
  }, [cargarTodo]);

  /* ----------- Unimos ----------- */
  const items = useMemo(() => {
    const baseNorm = (ausenciasBase || []).map((a) => ({
      ...a,
      _is_request: false,
      estado: aEstado(a),
    }));
    return [...baseNorm, ...solicitudes];
  }, [ausenciasBase, solicitudes]);

  /* ----------- KPIs ----------- */
  const counts = useMemo(() => {
    const acc = { PENDIENTE: 0, APROBADA: 0, RECHAZADA: 0 };
    for (const a of items) {
      const e = aEstado(a);
      if (acc[e] != null) acc[e]++;
    }
    return { ...acc, TODAS: items.length };
  }, [items]);

  const chipsEstado = useMemo(
    () => [
      {
        id: "PENDIENTE",
        label: "Pendientes",
        icon: "⏳",
        count: counts.PENDIENTE,
        activo: filtroEstado === "PENDIENTE",
      },
      {
        id: "APROBADA",
        label: "Aprobadas",
        icon: "✅",
        count: counts.APROBADA,
        activo: filtroEstado === "APROBADA",
      },
      {
        id: "RECHAZADA",
        label: "Rechazadas",
        icon: "❌",
        count: counts.RECHAZADA,
        activo: filtroEstado === "RECHAZADA",
      },
      {
        id: "TODAS",
        label: "Todas",
        icon: "📊",
        count: counts.TODAS,
        activo: filtroEstado === "TODAS",
      },
    ],
    [counts, filtroEstado]
  );

  /* ----------- Rango ----------- */
  const rangoActual = useMemo(() => {
    const now = new Date();
    let inicio = null,
      fin = null;

    if (rangoSeleccionado === "personalizado") {
      if (fechasPersonalizadas.inicio)
        inicio = new Date(`${fechasPersonalizadas.inicio}T00:00:00`);
      if (fechasPersonalizadas.fin)
        fin = new Date(`${fechasPersonalizadas.fin}T23:59:59`);
    } else if (["24h", "5d", "7d", "14d"].includes(rangoSeleccionado)) {
      const dias =
        rangoSeleccionado === "24h" ? 1 : Number(rangoSeleccionado.replace("d", ""));
      inicio = new Date(now.getTime() - dias * 24 * 60 * 60 * 1000);
      fin = now;
    }

    return { inicio, fin };
  }, [rangoSeleccionado, fechasPersonalizadas]);

  const tokens = useMemo(
    () => strip(busqueda).split(/\s+/).filter(Boolean),
    [busqueda]
  );

  /* ----------- Filtrado / orden ----------- */
  const itemsFiltrados = useMemo(() => {
    const byEstado = (a) =>
      filtroEstado === "TODAS" ? true : aEstado(a) === filtroEstado;

    const byTexto = (a) => {
      if (tokens.length === 0) return true;
      const { tipo, subtipo } = getTipoParaMostrar(a);
      const haystack = strip(
        [
          a.usuario_email,
          String(tipo || "").replace(/_/g, " "),
          String(subtipo || "").replace(/_/g, " "),
          a.motivo,
          a._is_request ? "solicitud" : "",
        ].join(" ")
      );
      return tokens.every((t) => haystack.includes(t));
    };

    const { inicio, fin } = rangoActual;
    const byRango = (a) => {
      if (!inicio && !fin) return true;
      const s = getFechaInicio(a);
      const e = getFechaFin(a) ?? s;
      const aStart = s ? new Date(s) : null;
      const aEnd = e ? new Date(e) : aStart;
      if (!aStart) return false;
      const rStart = inicio ?? new Date("1970-01-01T00:00:00");
      const rEnd = fin ?? new Date("2999-12-31T23:59:59");
      return aEnd >= rStart && aStart <= rEnd;
    };

    return (items || [])
      .filter(byEstado)
      .filter(byTexto)
      .filter(byRango)
      .sort(
        (a, b) =>
          new Date(getFechaFin(b) || getFechaInicio(b) || 0) -
            new Date(getFechaFin(a) || getFechaInicio(a) || 0) ||
          (b.id || 0) - (a.id || 0)
      );
  }, [items, filtroEstado, tokens, rangoActual]);

  useEffect(() => {
    setPagina(1);
  }, [filtroEstado, busqueda, rangoSeleccionado, fechasPersonalizadas, porPagina]);

  const totalPaginas = Math.max(1, Math.ceil(itemsFiltrados.length / porPagina));
  const desde = (pagina - 1) * porPagina;
  const visibles = itemsFiltrados.slice(desde, desde + porPagina);

  /* ----------- Render celda Fechas ----------- */
  const renderFechasCell = (a) => {
    // No CAMBIO → normal
    if (!(a._is_request && a._request_kind === "change")) {
      const fi =
        a.fecha_inicio ??
        a.payload?.fecha_inicio ??
        a.payload?.desde ??
        a.ausencia?.fecha_inicio ??
        null;
      const ff =
        a.fecha_fin ??
        a.payload?.fecha_fin ??
        a.payload?.hasta ??
        a.ausencia?.fecha_fin ??
        null;
      const rango = rangoFecha(fi, ff);
      const horas = a.parcial ? rangoHoras(a.hora_inicio, a.hora_fin) : null;
      return (
        <>
          <div>{rango}</div>
          {horas && <div className="text-xs text-slate-600">{horas}</div>}
        </>
      );
    }

    // CAMBIO: ORIGINAL prioriza original_* si existen; si no, la base
    const base = a.ausencia || {};
    const origFi = a.original_fecha_inicio ?? base.fecha_inicio ?? null;
    const origFf = a.original_fecha_fin ?? base.fecha_fin ?? null;
    const origHi = a.original_hora_inicio ?? base.hora_inicio ?? null;
    const origHf = a.original_hora_fin ?? base.hora_fin ?? null;
    const origParcial =
      typeof base.parcial === "boolean" ? base.parcial : false;

    // NUEVA: usa nueva_* si existen; si no, payload (defensivo); si falta, cae a original solo para mostrar
    const { fi: pFi, ff: pFf, hi: pHi, hf: pHf, parcial: pParcial } =
      extractPropuesta(a);
    const newFi = pFi ?? origFi;
    const newFf = pFf ?? origFf;
    const newHi = pHi ?? origHi;
    const newHf = pHf ?? origHf;
    const newParcial =
      typeof pParcial === "boolean" ? pParcial : origParcial;

    const sameDates = sameStr(origFi, newFi) && sameStr(origFf, newFf);
    const sameHours =
      origParcial === newParcial &&
      sameStr(origHi, newHi) &&
      sameStr(origHf, newHf);

    if (sameDates && sameHours) {
      const rango = rangoFecha(origFi, origFf);
      const horas = origParcial ? rangoHoras(origHi, origHf) : null;
      return (
        <>
          <div>{rango}</div>
          {horas && <div className="text-xs text-slate-600">{horas}</div>}
        </>
      );
    }

    const title = `Cambio solicitado: ${rangoFecha(origFi, origFf)} ${
      origParcial ? rangoHoras(origHi, origHf) || "" : ""
    } → ${rangoFecha(newFi, newFf)} ${
      newParcial ? rangoHoras(newHi, newHf) || "" : ""
    }`
      .replace(/\s+/g, " ")
      .trim();

    return (
      <div
        className="flex flex-col gap-0.5"
        title={title}
        aria-label="Cambio propuesto"
      >
        <div className="text-xs text-slate-500">
          <span className="mr-1">Original:</span>
          <span>{rangoFecha(origFi, origFf)}</span>
          {origParcial && rangoHoras(origHi, origHf) && (
            <span className="ml-1">{rangoHoras(origHi, origHf)}</span>
          )}
        </div>
        <div className="text-xs">
          <span className="mr-1 font-semibold text-sky-700">↦ Nueva:</span>
          <span className="font-medium text-slate-900">
            {rangoFecha(newFi, newFf)}
          </span>
          {newParcial && rangoHoras(newHi, newHf) && (
            <span className="ml-1 text-slate-800">
              {rangoHoras(newHi, newHf)}
            </span>
          )}
        </div>
      </div>
    );
  };

  /* ----------- Acciones ----------- */
  const pedirAprobar = (fila) => {
    setFilaSeleccionada(fila);
    setAprobarOpen(true);
  };
  const pedirRechazar = (fila) => {
    setFilaSeleccionada(fila);
    setMotivoRechazo("");
    setRechazarOpen(true);
  };

  const doAprobar = async () => {
    const a = filaSeleccionada;
    if (!a) return;
    try {
      if (a._is_request) {
        if (a._request_kind === "change") {
          await ausenciasService.aprobarCambio(token, a.id);
        } else if (a._request_kind === "cancel") {
          await ausenciasService.aprobarCancelacion(token, a.id);
        }
      } else {
        await ausenciasService.aprobar(token, a.id);
      }
      toast.success("Acción realizada ✅");
      setAprobarOpen(false);
      setFilaSeleccionada(null);
      cargarTodo();
    } catch (e) {
      toast.error(e?.message || "Error al aprobar");
    }
  };

  const doRechazar = async () => {
    const a = filaSeleccionada;
    if (!a) return;
    try {
      if (a._is_request) {
        if (a._request_kind === "change") {
          await ausenciasService.rechazarCambio(
            token,
            a.id,
            motivoRechazo.trim()
          );
        } else if (a._request_kind === "cancel") {
          await ausenciasService.rechazarCancelacion(
            token,
            a.id,
            motivoRechazo.trim()
          );
        }
      } else {
        await ausenciasService.rechazar(token, a.id, motivoRechazo.trim());
      }
      toast.info("Acción realizada");
      setRechazarOpen(false);
      setFilaSeleccionada(null);
      setMotivoRechazo("");
      cargarTodo();
    } catch (e) {
      toast.error(e?.message || "Error al rechazar");
    }
  };

  /* ----------- Texto barra ----------- */
  const estadoLabel =
    filtroEstado === "PENDIENTE"
      ? "Pendientes"
      : filtroEstado === "APROBADA"
      ? "Aprobadas"
      : filtroEstado === "RECHAZADA"
      ? "Rechazadas"
      : "Todas";

  const rangoLabel = (() => {
    if (rangoSeleccionado === "personalizado") {
      const { inicio, fin } = fechasPersonalizadas;
      if (inicio && fin)
        return `Personalizado: ${fmtISODateSafely(
          inicio
        )} → ${fmtISODateSafely(fin)}`;
      return "Personalizado";
    }
    const map = {
      "24h": "Últimas 24h",
      "5d": "Últimos 5 días",
      "7d": "Últimos 7 días",
      "14d": "Últimos 14 días",
      todos: "Todo",
    };
    return map[rangoSeleccionado] || "Todo";
  })();

  const microcopy = `Mostrando: ${estadoLabel} · ${rangoLabel} · ${
    itemsFiltrados.length
  } resultado${itemsFiltrados.length === 1 ? "" : "s"}${
    busqueda ? ` · Filtro: “${busqueda}”` : ""
  }`;

  /* =================== Render =================== */
  return (
    <div className="space-y-5">
      <BarraFiltros
        microcopy={microcopy}
        placeholderBusqueda="Buscar por usuario, tipo o motivo…"
        busqueda={busqueda}
        setBusqueda={(v) => {
          setBusqueda(v);
          setPagina(1);
        }}
        rangoSeleccionado={rangoSeleccionado}
        setRangoSeleccionado={(v) => {
          if (RANGOS.includes(v)) setRangoSeleccionado(v);
        }}
        fechasPersonalizadas={fechasPersonalizadas}
        setFechasPersonalizadas={(v) => setFechasPersonalizadas(v)}
        porPagina={porPagina}
        setPorPagina={(n) => setPorPagina(n)}
        chipsEstado={chipsEstado}
        onChipClick={(chip) => {
          setFiltroEstado(chip.id);
          setPagina(1);
        }}
        stickyTopClass="top-24"
      />

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-100/80 text-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Empleado</th>
              <th className="px-4 py-2 text-left">Tipo</th>
              <th className="px-4 py-2 text-left">Fechas</th>
              <th className="px-4 py-2 text-left">Parcial</th>
              <th className="px-4 py-2 text-left">Retribuida</th>
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-left">Motivo</th>
              <th className="px-4 py-2 text-left">Fecha solicitud</th>
              <th className="px-4 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  Cargando…
                </td>
              </tr>
            )}

            {!loading && visibles.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  No hay solicitudes que coincidan.
                </td>
              </tr>
            )}

            {!loading &&
              visibles.map((a) => {
                const { tipo, subtipo } = getTipoParaMostrar(a);
                const textoTipo = prettyTipo(tipo, subtipo);

                return (
                  <tr
                    key={`${a._is_request ? `req-${a._request_kind}-` : ""}${a.id}`}
                    className="border-t"
                  >
                    <td className="px-4 py-2 align-top">
                      <div className="font-medium text-slate-900">
                        {a.usuario_email || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className="capitalize">{textoTipo}</span>
                        {a._is_request && (
                          <span
                            className="text-[10px] rounded-lg px-2 py-0.5"
                            style={{
                              background:
                                a._request_kind === "cancel"
                                  ? "#fee2e2"
                                  : "#e0e7ff",
                              color:
                                a._request_kind === "cancel"
                                  ? "#991b1b"
                                  : "#3730a3",
                            }}
                          >
                            {a._request_kind === "cancel"
                              ? "Solicitud de Cancelación"
                              : "Solicitud De Cambio"}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-2 align-top">
                      {renderFechasCell(a)}
                    </td>

                    <td className="px-4 py-2 align-top">
                      {a.parcial ? "Sí" : "No"}
                    </td>
                    <td className="px-4 py-2 align-top">
                      {a.retribuida ? "Sí" : "No"}
                    </td>

                    <td className="px-4 py-2 align-top">
                      {(() => {
                        const raw =
                          a.estado ??
                          a.request_status ??
                          a.status ??
                          a._estado ??
                          a._status ??
                          "PENDING";
                        const map = {
                          PENDING: "PENDIENTE",
                          APPROVED: "APROBADA",
                          REJECTED: "RECHAZADA",
                          PENDIENTE: "PENDIENTE",
                          APROBADA: "APROBADA",
                          RECHAZADA: "RECHAZADA",
                        };
                        const est =
                          map[String(raw).toUpperCase()] ?? "PENDIENTE";
                        const cls =
                          est === "PENDIENTE"
                            ? "bg-yellow-100 text-yellow-800"
                            : est === "APROBADA"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800";
                        return (
                          <span
                            className={`inline-block rounded-lg px-2 py-1 text-xs font-semibold ${cls}`}
                          >
                            {est}
                          </span>
                        );
                      })()}
                    </td>

                    <td className="px-4 py-2 align-top">
                      {a.motivo || "—"}
                    </td>

                    <td className="px-4 py-2 align-top">
                      {a._is_request ? fmtFechaHora(a.created_at) : "—"}
                    </td>

                    <td className="px-4 py-2 align-top">
                      {aEstado(a) === "PENDIENTE" ? (
                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={() => pedirAprobar(a)}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                          >
                            Aprobar
                          </button>
                          <button
                            onClick={() => pedirRechazar(a)}
                            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700"
                          >
                            Rechazar
                          </button>
                        </div>
                      ) : (
                        <div className="text-right text-slate-400 text-xs">
                          —
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          totalItems={itemsFiltrados.length}
          porPagina={porPagina}
          onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
          onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
          onGoToPage={(n) => setPagina(n)}
          showFirstLast
        />
      )}

      <ConfirmModal
        visible={aprobarOpen}
        onClose={() => {
          setAprobarOpen(false);
          setFilaSeleccionada(null);
        }}
        onConfirm={doAprobar}
        mensaje="¿Confirmas la aprobación?"
      />

      {rechazarOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Rechazar</h3>
            <p className="text-sm text-slate-600">
              Indica el motivo del rechazo:
            </p>
            <textarea
              rows={4}
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Motivo…"
            />
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
              <button
                onClick={() => {
                  setRechazarOpen(false);
                  setFilaSeleccionada(null);
                  setMotivoRechazo("");
                }}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={doRechazar}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-rose-600 hover:bg-rose-700"
              >
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}